import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/contexts/auth-context';
import { useOnboarding } from '@/contexts/onboarding-context';
import { useUserContext } from '@/hooks/useUserContext';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  TrendingUp,
  Receipt,
  Settings,
  Target,
  Activity,
  Inbox,
  HandCoins,
  Clock,
  ChevronDown,
  ChevronRight,
  Scale,
  List,
  Brain,
  BarChart3,
  ArrowLeftRight,
  ScanText,
  Repeat,
  CheckSquare,
  RotateCcw,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CompanySelector } from './company-selector';
import { SidebarToggle } from './sidebar-toggle';
import { useSidebar } from '@/contexts/sidebar-context';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AccessCollectIcon } from '@/components/icons/AccessCollectIcon';
import { useScreenModes } from '@/providers/ScreenModesProvider';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { useCompany } from '@/contexts/CompanyContext';
import { getCurrencyIcon } from '@/components/shared/currency-icon';
// Type for lucide-react icon components
type IconComponent = React.ComponentType<{ className?: string }>;

function shouldHideNavItem(navItem: NavigationItem, isSystemAdmin: boolean): boolean {
  if (!isSystemAdmin && navItem.name === 'Settings') return true;
  if (!isSystemAdmin && (navItem.href === '/users' || navItem.href === '/integrations')) return true;
  return false;
}

function filterNavItemSubItems(navItem: NavigationItem, canUseDemoData: boolean): NavigationItem {
  if (!canUseDemoData && navItem.subItems) {
    return { ...navItem, subItems: navItem.subItems.filter((s) => s.name !== 'Screen Data Modes') };
  }
  return navItem;
}

interface NestedNavigationItem {
  name: string;
  href: string;
  icon: IconComponent;
  badge?: string;
}

interface SubNavigationItem {
  name: string;
  href?: string;
  icon: IconComponent;
  badge?: string;
  isExpandable?: boolean;
  subItems?: NestedNavigationItem[];
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: IconComponent;
  badge?: string;
  isExpandable?: boolean;
  subItems?: SubNavigationItem[];
}

function getNavigation(companyCurrency?: string): NavigationItem[] {
  return [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'My Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Transactions', href: '/transactions', icon: ArrowLeftRight },
  {
    name: 'Payments',
    icon: CreditCard,
    isExpandable: true,
    subItems: [
      {
        name: 'Receipts',
        href: '/payments/receipts',
        icon: getCurrencyIcon(companyCurrency),
      },

    ],
  },
  {
    name: 'Collections',
    icon: Target,
    isExpandable: true,
    subItems: [
      {
        name: 'Dashboard',
        href: '/collections/dashboard',
        icon: Activity,
      },
      {
        name: 'Collections Queue',
        href: '/collections/queue',
        icon: Inbox,
      },
      {
        name: 'Promise to Pay',
        href: '/collections/promises',
        icon: HandCoins,
      },
    ],
  },
  {
    name: 'Dispute Management',
    icon: Scale,
    isExpandable: true,
    subItems: [
      {
        name: 'Dashboard',
        href: '/disputes',
        icon: Activity,
      },
      {
        name: 'All Disputes',
        href: '/disputes/all',
        icon: List,
      },
    ],
  },
  {
    name: 'Analytics & Reporting',
    icon: BarChart3,
    isExpandable: true,
    subItems: [
      {
        name: 'Ageing Analysis',
        href: '/analytics-reporting/ageing-analysis',
        icon: TrendingUp,
      },
      {
        name: 'Multi-Company Dashboard',
        href: '/analytics-reporting/multi-company',
        icon: LayoutDashboard,
      },
      {
        name: 'Emailing',
        href: '/analytics-reporting/emailing',
        icon: Mail,
      },
    ],
  },
  {
    name: 'Settings',
    icon: Settings,
    isExpandable: true,
    subItems: [
      {
        name: 'Company',
        href: '/settings/company',
        icon: LayoutDashboard,
      },
      {
        name: 'Users',
        href: '/settings/users',
        icon: Users,
      },
      {
        name: 'General',
        href: '/settings/general',
        icon: Settings,
      },
      // {
      //   name: 'Integrations',
      //   href: '/integrations',
      //   icon: Plug,
      // },
      {
        name: 'Templates',
        href: '/settings/templates',
        icon: Receipt,
      },
      {
        name: 'Collection Strategies',
        href: '/settings/strategies',
        icon: Clock,
      },
      {
        name: 'Dispute Types',
        href: '/settings/dispute-types',
        icon: Scale,
      },
      {
        name: 'Screen Data Modes',
        href: '/settings/screen-data-modes',
        icon: Activity,
      },
    ],
  },
];
}

export function AppSidebar() {
  const { selectedCompany } = useCompany();
  const navigation = getNavigation(selectedCompany?.currency);
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const auth = useContext(AuthContext);
  const { canUseDemoData } = useScreenModes();
  const { openWizard, hasCompletedOnboarding, savedStep } = useOnboarding();
  const { data: userContext } = useUserContext();
  const { isAdmin: isSystemAdmin } = useIsAdmin();
  const isAdmin = auth?.user.role === 'administrator';
  const companyCount = userContext?.companies?.length ?? 0;
  // Hide once the user has properly finished ("Finish Setup"). Skipping leaves it visible.
  // Also hide when has companies with no onboarding in progress.
  const showResumeOnboarding = isAdmin && !hasCompletedOnboarding && (companyCount === 0 || savedStep > 0);
  // Changed to store multiple expanded items for nested menu support
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Auto-expand the appropriate menu item when navigating to its routes
  useEffect(() => {
    const itemsToExpand = new Set<string>();

    if (location.pathname.startsWith('/settings') || location.pathname.startsWith('/users') || location.pathname.startsWith('/integrations')) {
      itemsToExpand.add('Settings');
    } else if (location.pathname.startsWith('/disputes')) {
      itemsToExpand.add('Dispute Management');
    } else if (location.pathname.startsWith('/collections')) {
      itemsToExpand.add('Collections');
    } else if (location.pathname.startsWith('/analytics-reporting')) {
      itemsToExpand.add('Analytics & Reporting');
    } else if (location.pathname.startsWith('/payments')) {
      itemsToExpand.add('Payments');
    }

    if (itemsToExpand.size > 0) {
      setExpandedItems(itemsToExpand);
    }
  }, [location.pathname]);

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const isActive = (href: string) => {
    // For root path, exact match only
    if (href === '/') {
      return location.pathname === '/';
    }

    // Exact match
    if (location.pathname === href) {
      return true;
    }

    // Check if current path starts with this href (for child routes)
    if (location.pathname.startsWith(href + '/')) {
      // Now check if there's a MORE specific match in the navigation
      // (i.e., another menu item with a longer matching path)
      const hasMoreSpecificMatch = navigation.some(item => {
        // Check direct items
        if (item.href && item.href !== href && item.href.length > href.length && location.pathname.startsWith(item.href)) {
          return true;
        }
        // Check subitems
        if (item.subItems) {
          return item.subItems.some(subItem => {
            if (subItem.href && subItem.href !== href && subItem.href.length > href.length && location.pathname.startsWith(subItem.href)) {
              return true;
            }
            return false;
          });
        }
        return false;
      });

      // Only highlight if there's no more specific match
      return !hasMoreSpecificMatch;
    }

    return false;
  };

  const isParentActive = (item: NavigationItem) => {
    if (!item.subItems) return false;
    return item.subItems.some((subItem) => subItem.href ? isActive(subItem.href) : false);
  };

  const renderExpandableSubItem = (subItem: SubNavigationItem) => {
    if (!subItem.isExpandable || !subItem.subItems) return null;
    const isSubExpanded = expandedItems.has(subItem.name);
    const subParentActive = subItem.subItems.some((nestedItem) => nestedItem.href && isActive(nestedItem.href));

    return (
      <div key={subItem.name}>
        <button
          onClick={() => toggleExpanded(subItem.name)}
          className={cn(
            'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors truncate',
            subParentActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
        >
          <div className="flex items-center gap-3">
            <subItem.icon className="h-4 w-4" />
            {subItem.name}
            {subItem.badge && (
              <Badge variant="outline" className="h-5 px-1.5 text-xs border-destructive text-destructive">
                {subItem.badge}
              </Badge>
            )}
          </div>
          {isSubExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {isSubExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l pl-4">
            {subItem.subItems.map((nestedItem) => {
              const nestedItemActive = isActive(nestedItem.href);
              return (
                <Link
                  key={nestedItem.name}
                  to={nestedItem.href}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors truncate',
                    nestedItemActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <nestedItem.icon className="h-3 w-3" />
                    {nestedItem.name}
                  </div>
                  {nestedItem.badge && (
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                      {nestedItem.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderExpandableNavItem = (item: NavigationItem) => {
    if (!item.subItems) return null;
    const isExpanded = expandedItems.has(item.name);
    const parentActive = isParentActive(item);
    const subItems = item.subItems;

    return (
      <div key={item.name}>
        {isCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={subItems[0]?.href || '#'}
                className={cn(
                  'flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  parentActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-normal">
              {item.name}
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            onClick={() => toggleExpanded(item.name)}
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap',
              parentActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <div className="flex items-center gap-3">
              <item.icon className="h-5 w-5" />
              {item.name}
              {item.badge && (
                <Badge variant="outline" className="h-5 px-1.5 text-xs border-destructive text-destructive">
                  {item.badge}
                </Badge>
              )}
            </div>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        )}
        {isExpanded && !isCollapsed && (
          <div className="ml-4 mt-1 space-y-1 border-l pl-4">
            {subItems.map((subItem) => {
              if (subItem.isExpandable && subItem.subItems) {
                return renderExpandableSubItem(subItem);
              }
              const subItemActive = subItem.href ? isActive(subItem.href) : false;
              return (
                <Link
                  key={subItem.name}
                  to={subItem.href || '#'}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors truncate',
                    subItemActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <subItem.icon className="h-4 w-4" />
                    {subItem.name}
                  </div>
                  {subItem.badge && (
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                      {subItem.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderRegularNavItem = (item: NavigationItem) => {
    const itemActive = item.href ? isActive(item.href) : false;
    if (isCollapsed) {
      return (
        <Tooltip key={item.name}>
          <TooltipTrigger asChild>
            <Link
              to={item.href || '#'}
              className={cn(
                'flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                itemActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <item.icon className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="font-normal">
            {item.name}
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Link
        key={item.name}
        to={item.href || '#'}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          itemActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
      >
        <item.icon className="h-5 w-5" />
        {item.name}
        {item.badge && (
          <span className={cn(
            "ml-auto px-2 py-0.5 text-xs font-medium rounded",
            item.badge === 'Live'
              ? "bg-green-500 text-white"
              : "bg-secondary text-secondary-foreground"
          )}>
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "border-r bg-card flex flex-col h-screen transition-all duration-300",
          isCollapsed ? "w-16" : "w-72"
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-6">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <AccessCollectIcon className="h-12 w-12" />
              <h1 className="text-xl font-bold">Access Collect</h1>
            </div>
          )}
          <SidebarToggle />
        </div>
        {!isCollapsed && <CompanySelector />}
        <nav className="space-y-1 p-4 flex-1 overflow-y-auto">
          {navigation.map((navItem) => {
            if (shouldHideNavItem(navItem, isSystemAdmin)) return null;
            const item = filterNavItemSubItems(navItem, canUseDemoData);
            if (item.isExpandable && item.subItems) {
              return renderExpandableNavItem(item);
            }
            return renderRegularNavItem(item);
          })}
        </nav>

        {/* Onboarding re-entry — visible to admins with partial/incomplete onboarding */}
        {showResumeOnboarding && (
          <div className="border-t p-3">
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={openWizard}
                    className="flex w-full items-center justify-center rounded-lg px-3 py-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Resume Onboarding</TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={openWizard}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-4 w-4 shrink-0" />
                <span>Resume Onboarding</span>
              </button>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
